<template lang="html">
<div class="search-bar">
    <v-text-field
    class="search-input"
    light
    solo
    clearable
    prepend-icon="search"
    placeholder="Search"
    @input="startLoading(); onInput()"
    v-model="query"
    ></v-text-field>
    <transition name="fade">
        <v-progress-linear
        class="ma-0 searching-indicator"
        v-show="searching"
        :indeterminate="true"
        height="4"
        color="accent"
        ></v-progress-linear>
    </transition>
    <transition name="fade">
    <div
    class="results white primary--text elevation-4"
    v-show="showResults"
    >
        <v-list two-line light>
        <div v-if="coordsQuery">
            <v-subheader class="grey--text text-x-darken-2">Latitude, Longitude</v-subheader>
            <v-list-tile avatar @click="goToCoordinates(coords)">
                <v-list-tile-avatar>
                    <v-icon class="orange white--text">location_searching</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                    <v-list-tile-title>
                        {{ coords.lat | numFormat('0.00000') }},
                        {{ coords.lng | numFormat('0.00000') }}
                    </v-list-tile-title>
                    <v-list-tile-sub-title>Go to coordinates</v-list-tile-sub-title>
                </v-list-tile-content>
            </v-list-tile>
        </div>
            <v-subheader class="grey--text text-x-darken-2">Search results</v-subheader>
            <v-list-tile v-if="results.length === 0">
                <v-list-tile-content>
                    <v-list-tile-title v-if="!searching"><em>No results found</em></v-list-tile-title>
                    <v-list-tile-title v-if="searching"><em>Searching...</em></v-list-tile-title>
                </v-list-tile-content>
            </v-list-tile>
            <div v-for="result in results" :key="result._id">
                <v-list-tile avatar @click="selectResult(result)">
                    <v-list-tile-avatar v-if="result.type === 'wfis'">
                        <icon-watershed />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'wfis'">
                        <v-list-tile-title>{{ result._source.wfi }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'licences'">
                        <icon-watershed />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'licences'">
                        <v-list-tile-title>{{ result._source.licence_no }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'geonames'">
                        <icon-geoname />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'geonames'">
                        <v-list-tile-title>{{ result._source.geoname }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'groundwater level'">
                        <icon-gw-level />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'groundwater level'">
                        <v-list-tile-title>{{ result._source.station_name }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'climate'">
                        <icon-climate />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'climate'">
                        <v-list-tile-title>{{ result._source.station_name }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'streamflow monitoring'">
                        <icon-streamflow />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'streamflow monitoring'">
                        <v-list-tile-title>{{ result._source.station_name }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'surface water quality'">
                        <icon-sw-quality />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'surface water quality'">
                        <v-list-tile-title>{{ result._source.station_name }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-avatar v-if="result.type === 'groundwater quality'">
                        <icon-gw-quality />
                    </v-list-tile-avatar>
                    <v-list-tile-content v-if="result.type === 'groundwater quality'">
                        <v-list-tile-title>{{ result._source.station_name }}</v-list-tile-title>
                    </v-list-tile-content>
                </v-list-tile>
                <v-divider inset></v-divider>
            </div>
        </v-list>
    </div>
    </transition>
</div>
</template>

<script>
import gql from 'graphql-tag';
import _ from 'lodash';

import * as actions from '@/constants/actions';
import { apolloClient } from '@/apollo/apollo';
import MapMixin from '@/mixins/map';

import iconStreamflow from '@/components/icons/Streamflow';
import iconWatershed from '@/components/icons/Watershed';
import iconSwQuality from '@/components/icons/SwQuality';
import iconGwQuality from '@/components/icons/GwQuality';
import iconGwLevel from '@/components/icons/GwLevel';
import iconClimate from '@/components/icons/Climate';
import iconGeoname from '@/components/icons/Geoname';

export default {
    name: 'search-bar',
    mixins: [
        MapMixin,
    ],
    components: {
        iconWatershed,
        iconSwQuality,
        iconGwQuality,
        iconGwLevel,
        iconClimate,
        iconStreamflow,
        iconGeoname,
    },
    data() {
        return {
            coordsRegExp: /^(-?\d{1,3}(\.\d+)?),\s*(-?\d{1,3}(\.\d+)?)$/,
            query: '',
            results: [],
            // local loading indicator
            searching: false,
        };
    },
    computed: {
        /**
         * return true if the search query looks like a coordinates string
         * @return {Boolean} true for coordinates
         */
        coordsQuery() {
            return this.coordsRegExp.test(this.query);
        },
        coords() {
            if (!this.coordsQuery) {
                return { lat: 0, lng: 0 };
            }
            const matches = this.query.match(this.coordsRegExp);
            return {
                lat: Number(matches[1]),
                lng: Number(matches[3]),
            };
        },
        showResults() {
            return Boolean(this.query);
        },
    },
    methods: {
        /**
         * set searching = true if there's a search query string,
         * otherwise set it false
         */
        startLoading() {
            if (this.query) {
                this.searching = true;
            } else {
                this.searching = false;
            }
        },
        // handle search input and run the query if applicable
        onInput: _.debounce(function typing() {
            if (this.query) {
                this.getResults();
            }
        }, 200),
        // for demo purposes, do a fuzzymatch search on the local data
        async getResults() {
            try {
                const { data } = await apolloClient.query({
                    query: gql`
                    query($q: String!, $offset: Int!, $limit: Int!) {
                        search(q: $q, offset: $offset, limit: $limit)
                    }
                `,
                    variables: {
                        q: this.query,
                        offset: 0,
                        limit: 5,
                    },
                });
                this.results = data.search.hits.hits.map((item) => {
                    let type = item._source.type;
                    if (type === 'stations') {
                        const { geocomment } = item._source;
                        type = [
                            'groundwater level',
                            'climate',
                            'streamflow monitoring',
                            'surface water quality',
                            'groundwater quality',
                        ].find(i => i === geocomment.toLowerCase());
                    }
                    return {
                        _id: item._id,
                        _source: item._source,
                        type,
                    };
                });
                this.searching = false;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving search results');
            }
        },
        /**
         * focus the map on the selected feature, and show the sidebar
         * @param  {Object} result selected feature
         */
        selectResult(result) {
            window.gtag('event', 'Search', { action: 'selectresult', label: 'User selected a result from a search result' });
            const { id, type, x: lng, y: lat } = result._source;
            const currentRoutePath = this.$route.path;

            if (type === 'stations') {
                window.gtag('event', 'Search', { action: 'selectstation', label: 'User chose station from search' });
                let route = {
                    'groundwater level': 'groundwater-level',
                    climate: 'climate',
                    'streamflow monitoring': 'streamflow',
                    'surface water quality': 'surface-water-quality',
                    'groundwater quality': 'groundwater-quality',
                }[result._source.geocomment.toLowerCase()];
                route = `/${route}`;

                if (currentRoutePath !== route) {
                    // store selected station in the loadState module
                    this.$store.dispatch(actions.SET_LOADSTATE_SELECTED_FEATURE, {
                        id,
                        coords: [
                            lng,
                            lat,
                        ],
                        type: 'station',
                    });
                    // redirect to new page,
                    // which will then select it from the loadState module
                    this.$router.push(route);
                } else {
                    // select licence on this page
                    this.$store.dispatch(actions.SELECT_STATION, {
                        id,
                        coords: [
                            lng,
                            lat,
                        ],
                    });
                    this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
                }
            } else if (type === 'licences') {
                window.gtag('event', 'Search', { action: 'selectwfi', label: 'User chose licence from search' });
                if (currentRoutePath !== '/watershed') {
                    // store selected licence in the loadState module
                    this.$store.dispatch(actions.SET_LOADSTATE_SELECTED_FEATURE, {
                        id,
                        coords: [
                            lng,
                            lat,
                        ],
                        type: 'licence',
                    });
                    // redirect to watersheds page,
                    // which will then select it from the loadState module
                    this.$router.push('/watershed');
                } else {
                    // select licence on this page
                    this.$store.dispatch(actions.SELECT_LICENCE, {
                        id,
                        coords: [
                            lng,
                            lat,
                        ],
                    });
                    this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
                }
            } else if (type === 'geonames') {
                window.gtag('event', 'Search', { action: 'selectwfi', label: 'User chose geoname from search' });
                this.selectGeoname({ lat, lng }, result._source.zoom);
            } else if (type === 'wfis') {
                window.gtag('event', 'Search', { action: 'selectwfi', label: 'User chose wfi from search' });

                const wfi = result._source.wfi;

                const coords = [
                    result._source.lng,
                    result._source.lat,
                ];

                if (currentRoutePath !== '/watershed') {
                    // store selected watershed in the loadState module
                    this.$store.dispatch(actions.SET_LOADSTATE_SELECTED_FEATURE, {
                        id: wfi,
                        coords,
                        type: 'watershed',
                    });
                    // redirect to watersheds page,
                    // which will then select it from the loadState module
                    this.$router.push('/watershed');
                } else {
                    // select licence on this page
                    this.$store.dispatch(actions.SELECT_WATERSHED, {
                        id: wfi,
                        coords,
                    });
                    this.$store.dispatch(actions.SHOW_MAP_SIDEBAR, true);
                }
            }
            // clear search and hide results
            this.query = '';
        },
        selectGeoname(coords, zoom) {
            this.goToCoordinates(coords, zoom);
            this.query = '';
        },
        /**
         * set the map center in vuex
         * @param {Object} coords object with lng, lat
         * @param {Number} zoom level
         */
        async goToCoordinates(coords, zoom = 14) {
            window.gtag('event', 'Search', { action: 'gotocoordinates', label: 'User chose coordinates from search' });

            const { lng, lat } = coords;

            if (lat > 90 || lat < -90) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Latitude must be between -90 and 90');
                return;
            }

            const map = await this.mapPromise;
            const lngLat = {
                lng,
                lat,
            };

            // once the flyTo is done,
            map.once('moveend', () => {
                // fire event click at these coordinates
                map.fire('click', {
                    lngLat,
                    originalEvent: {}, // required
                });
            });

            // animate to these coordinates
            map.flyTo({
                center: lngLat,
                zoom,
            });

            // clear query to hide the results
            this.query = '';
        },
    },
};
</script>

<style lang="scss">
.search-bar {
    flex-basis: 25rem;
    flex-shrink: 2;
    position: relative;

    .list--two-line .list__tile {
        height: 50px;
    }

    .search-input {
        // show above results
        z-index: 1;
        .input-group__input {
            flex-shrink: 1; // IE11 fix
        }
    }

    .results {
        position: absolute;
        margin-top: -.5rem;
        width: 100%;
    }

    svg {
        height: 2.8rem;
        width: 2.8rem;
    }

    .searching-indicator {
        position: absolute;
        z-index: 1; // to show the background shade
        bottom: 0px;
    }

    // fade transition
    .fade-enter-active, .fade-leave-active {
      transition: opacity .25s
    }
    .fade-enter, .fade-leave-to {
      opacity: 0
    }

}
</style>



// WEBPACK FOOTER //
// src/components/Search.vue